import React, { useState } from "react";
import { useSelector } from "react-redux";
import {
  Text,
  Button,
  Group,
  Stack,
  Divider,
  ScrollArea,
  Popover,
  Box,
  TextInput,
  UnstyledButton,
  NumberInput
} from "@mantine/core";
import Swal from "sweetalert2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";

import FilterSort from './FilterSort';
import FilterOptions from './FilterOptions';
import FilterVisibility from './FilterVisibility';
import FilterIconSelector from './FilterIconSelector';

import SpaceMemberFilters from '../../../../models/SpaceMemberFilters';

import { useDebounce } from "../../../hooks/useDebounce";
import {
  ALL_FILTER_OPTIONS,
  ALL_FILTER_MEMBER_VISIBILITY,
  ALL_FILTER_PROPERTIES,
  generateUniqueName,
  generateKeyFromName,
  getOptionKey,
  sortByName
} from "../../../../utils/Filters";

export default function FilterDefinitionEditor({ slug, type, allFilters, handlers, selectedProperty, setSelectedProperty }) {
  const [popoverOpened, setPopoverOpened] = useState(false);
  const [search, setSearch] = useState("");
  const [activeTypeLabel, setActiveTypeLabel] = useState(null);
  const [filterUploading, setFilterUploading] = useState(false);

  const colors = useSelector((state) => state.colors);

  const filteredOptions = ALL_FILTER_OPTIONS.filter((option) =>
    option.label.toLowerCase().includes(search.toLowerCase())
  );

  const FilterModel = type === "filter-members" ? SpaceMemberFilters : null;

  const handleAddProperty = (option) => {
    if (!option || !slug) return;

    const filter_name = generateUniqueName(option.label, allFilters);
    const filter_key = generateKeyFromName(filter_name);

    const option_key = getOptionKey(option.value);

    const params = {
      name: filter_name,
      sort_order: allFilters.length,
      key: filter_key,
      format: option.value,
      options: option.options ? option.options : {
        [option_key]: ALL_FILTER_PROPERTIES[option.value]?.default_value || "",
        values: []
      },
      visibility: ["card", "profile"]
    };

    setFilterUploading(true);
    FilterModel.onCreate(
      slug,
      params,
      (data) => {
        setSearch("");
        setPopoverOpened(false);
        setFilterUploading(false);
        if (data?.id) {
          handlers.append(data);
          setSelectedProperty(data);
        }
      },
      (error) => {
        console.log("ERROR MEMBERS FILTER:: ", error);
        setFilterUploading(false);
      }
    );
  };

  const onFilterUpdate = (id, params) => {
    FilterModel.onUpdate(
      slug,
      id,
      params,
      (data) => {
        console.log("UPDATED FILTER:: ", data);
        if (data?.id) {
          handlers.setState((current) =>
            current.map((item) => item.id === data.id ? data : item)
          );
        }
      },
      (error) => {
        console.log("ERROR MEMBERS FILTER:: ", error);
      }
    );
  };

  const handleDelete = () => {
    Swal.fire({
      title: "Are you sure?",
      html: "All member data and any associated survey questions will be permanently deleted.",
      showDenyButton: true,
      confirmButtonText: "Yes",
      denyButtonText: "No",
      confirmButtonColor: colors.red,
      denyButtonColor: colors.gray
    }).then((result) => {
      if (result.isConfirmed && selectedProperty) {
        handlers.remove(allFilters.findIndex((i) => i.id === selectedProperty.id));
        setSelectedProperty(null);
        setActiveTypeLabel(null);
        FilterModel.onDestroy(
          slug,
          selectedProperty.id,
          (data) => {},
          (error) => {}
        );
      } else {
        setSelectedProperty(selectedProperty);
      }
    });
  };

  const handleDuplicate = () => {
    const option = {
      label: selectedProperty.name,
      value: selectedProperty.format,
      options: selectedProperty.options
    };
    handleAddProperty(option);
  };

  const onUpdateOptionsSort = (value) => {
    if (!selectedProperty?.id) return;
    const option_key = getOptionKey(selectedProperty.format);
    const sortedValues = sortByName(selectedProperty.options.values, value);
    const options = {
      ...selectedProperty.options,
      values: sortedValues,
      [option_key]: value
    };
    onFilterUpdate(selectedProperty.id, { options });
    setSelectedProperty({...selectedProperty, options});
  };

  const onUpdateOption = (value, isReorder) => {
    if (!value || !selectedProperty?.id) return;
    let values = [];

    if (Array.isArray(value)) {
      values = value;
    } else {
      const oldValues = Array.isArray(selectedProperty.options?.values) ? selectedProperty.options.values : [];
      const option_name = generateUniqueName(value, oldValues);
      const newOption = {
        id: generateKeyFromName(option_name),
        value: generateKeyFromName(option_name),
        name: option_name,
        sort_order: oldValues.length,
        visible: true,
        color: "#f3f3f3"
      };
      values = [...oldValues, newOption]
    }
  
    const sort = isReorder ? { sort: "manual" } : {};
    const options = { ...selectedProperty.options, ...sort, values };
    onFilterUpdate(selectedProperty.id, { options });
    setSelectedProperty({...selectedProperty, options});
  };

  const onChangeVisibility = (values) => {
    if (!selectedProperty?.id) return;
    onFilterUpdate(selectedProperty.id, { visibility: values });
    setSelectedProperty({...selectedProperty, visibility: values});
  };

  const onChangeEmojiicon = (value) => {
    if (!selectedProperty?.id) return;
    onFilterUpdate(selectedProperty.id, { emojicon: value });
    setSelectedProperty({...selectedProperty, emojicon: value});
  };

  const debouncedSave = useDebounce(({ id, value }) => {
    if (value.length === 0) return;
    const filter_name = generateUniqueName(value, allFilters);
    const filter_key = generateKeyFromName(filter_name);
    onFilterUpdate(id, { name: filter_name, key: filter_key });
  }, 1500);

  return (
    <Stack spacing="xs">
      <Text size="sm" color="grey">
        {"Please select the type of property you would like to add."}
      </Text>
      <Button
        variant="default"
        onClick={() => {
          setPopoverOpened(true);
          setSelectedProperty(null);
        }}
        leftIcon={<FontAwesomeIcon icon={solid("plus")} color="grey" />}
        radius="md"
        size="xs"
        style={{ width: 130 }}
      >
        <Text color="grey" size="xs">Add property</Text>
      </Button>

      <Popover
        opened={popoverOpened}
        onClose={() => setPopoverOpened(false)}
        position="bottom"
        placement="start"
        spacing={0}
        width={220}
        radius="md"
        transitionDuration={0}
      >
        <ScrollArea style={{ height: "auto" }} scrollbarSize={8} py={10}>
          {/*
            <TextInput
              placeholder="Search property"
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
              m={10}
            />
          */}
          <Text size="sm" color="grey" ml={10} mb={5}>Type</Text>
          <Stack spacing={2}>
            {filteredOptions.map((option) => (
              <Group
                key={option.value}
                spacing="sm"
                ml={8}
                mr={8}
                onClick={() => {
                  if (!filterUploading) {
                    if (selectedProperty?.id) {
                      const filter_name = generateUniqueName(option.label, allFilters);
                      const filter_key = generateKeyFromName(filter_name);
                      const option_key = getOptionKey(option.value);
                      const new_params = {
                        name: filter_name,
                        key: filter_key,
                        format: option.value,
                        options: option.options ? option.options : {
                          [option_key]: ALL_FILTER_PROPERTIES[option.value]?.default_value || "",
                          values: []
                        },
                      };
                      onFilterUpdate(selectedProperty.id, new_params);
                      setSelectedProperty({...selectedProperty, ...new_params});
                      setPopoverOpened(null);
                    } else {
                      handleAddProperty(option);
                    }
                  }
                }}
                sx={{ cursor: "pointer", padding: 6, borderRadius: 4, '&:hover': { backgroundColor: '#f5f5f5' } }}
              >
                <FontAwesomeIcon icon={solid("bolt")} size="sm" />
                <Text>{option.label}</Text>
              </Group>
            ))}
          </Stack>
        </ScrollArea>
      </Popover>

      <Popover
        opened={!!selectedProperty && !popoverOpened}
        onClose={() => {
          setSelectedProperty(null);
          setActiveTypeLabel(null);
        }}
        position="left"
        placement="start"
        width={250}
        transitionDuration={0}
        sx={{ left: -40 }}
      >
        <Box>
          <Group mb="md" spacing={0}>
            <FilterIconSelector emojicon={selectedProperty?.emojicon} onChangeEmojiicon={onChangeEmojiicon} />
            <TextInput
              value={typeof activeTypeLabel === "string" ? activeTypeLabel : (selectedProperty?.name || "")}
              style={{ flex: 1, marginLeft: 15 }}
              onChange={(e) => {
                setActiveTypeLabel(e.currentTarget.value);
                debouncedSave({ id: selectedProperty.id, value: e.currentTarget.value });
              }}
            />
          </Group>

          <Group position="apart" mt="sm" mb={10}>
            <Text size="sm">Type</Text>
            <Group spacing={4} style={{ cursor: "pointer" }} onClick={() => setPopoverOpened(true)}>
              <Text size="xs" color="grey">
                {ALL_FILTER_OPTIONS.find((item) => item.value === selectedProperty?.format)?.label}
              </Text>
              <FontAwesomeIcon icon={solid("chevron-right")} size="xs" color="grey" />
            </Group>
          </Group>

          {ALL_FILTER_PROPERTIES[selectedProperty?.format]?.sort && (
            <FilterSort
              options={ALL_FILTER_PROPERTIES[selectedProperty?.format].sort}
              value={selectedProperty.options?.sort || ALL_FILTER_PROPERTIES[selectedProperty.format].default_value}
              onChange={onUpdateOptionsSort}
            />
          )}

          {ALL_FILTER_PROPERTIES[selectedProperty?.format]?.format && (
            <FilterSort
              options={ALL_FILTER_PROPERTIES[selectedProperty?.format].format}
              value={selectedProperty.options?.format || ALL_FILTER_PROPERTIES[selectedProperty.format].default_value}
              onChange={onUpdateOptionsSort}
            />
          )}

          <FilterVisibility
            options={ALL_FILTER_MEMBER_VISIBILITY}
            values={selectedProperty?.visibility}
            onChange={onChangeVisibility}
          />

          {/*ALL_FILTER_PROPERTIES[selectedProperty?.format]?.decimalPlaces && (
            <FilterSort
              options={ALL_FILTER_PROPERTIES[selectedProperty?.format].decimalPlaces.map(String)}
              value={String(decimalPlaces)}
              // onChange={(item) => setDecimalPlaces(Number(item.value))}
              onChange={() => {}}
            />
          )*/}

          {ALL_FILTER_PROPERTIES[selectedProperty?.format]?.limit && (
            <Box mb="xs">
              <Text size="xs" transform="uppercase" weight={600} mb={4}>Limit</Text>
              <NumberInput size="xs" min={1} max={100} defaultValue={10} />
            </Box>
          )}

          {ALL_FILTER_PROPERTIES[selectedProperty?.format]?.show_options && (
            <>
              <Divider my="lg" />
              <FilterOptions
                options={Array.isArray(selectedProperty?.options?.values) ? selectedProperty.options.values : []}
                onUpdateOption={onUpdateOption}
              />
            </>
          )}

          <Divider my="md" />

          <UnstyledButton
            onClick={handleDuplicate}
            style={{ display: "flex", alignItems: "center" }}
            mb={10}
          >
            <FontAwesomeIcon icon="clone" color="grey" width={20} />
            <Text size="sm" color="grey" pl={10}>Duplicate property</Text>
          </UnstyledButton>

          <UnstyledButton
            onClick={handleDelete}
            style={{ display: "flex", alignItems: "center" }}
          >
            <FontAwesomeIcon icon="trash" color="grey" width={20} />
            <Text size="sm" color="grey" pl={10}>Delete property</Text>
          </UnstyledButton>
        </Box>
      </Popover>
    </Stack>
  );
}
